import { FC, ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis
} from 'recharts';

const COLORS = [
  'var(--color-primary-500)',
  'var(--color-secondary-clementine-500)',
  'var(--color-secondary-seafoam-500)',
  'var(--color-secondary-sky-blue-500)'
];

type MetricsEntryProps = {
  label: string;
  subLabel?: string;
  data: { value: number; label: string }[];
  colorOverrides?: string[];
};

const CustomLabel = (props: any) => {
  const { x, y, value } = props;
  return (
    <Text
      x={x + props.width / 2}
      y={y - 10}
      fill="var(--color-neutral-500)"
      textAnchor="middle"
      dominantBaseline="bottom"
      className="font-bold text-lg"
    >
      {`${value}%`}
    </Text>
  );
};

const MetricsBarChartEntry: FC<MetricsEntryProps> = ({
  label,
  subLabel,
  data,
  colorOverrides
}): ReactElement => {
  // Convert string values to numbers for the chart
  const chartData = data.map((item) => ({
    label: item.label,
    value: item.value
  }));

  const CustomXAxisTick = ({
    x,
    y,
    payload
  }: {
    x: number;
    y: number;
    payload: { value: string };
  }) => {
    if (payload?.value) {
      return (
        <Text
          width={180}
          x={x}
          y={y + 5}
          textAnchor="middle"
          verticalAnchor="start"
          fill="var(--color-neutral-500)"
          className="text-xs"
          maxLines={1}
        >
          {payload.value}
        </Text>
      );
    }
    return null;
  };

  return (
    <div
      className={`
        relative flex w-438 h-306 p-20 flex-col items-start gap-2.5
        shrink-0 bg-white rounded-sm elevated
      `}
    >
      <div className="flex justify-between mb-10">
        <h3 className="text-h3">{label}</h3>
      </div>
      <div className="flex w-full">
        <ResponsiveContainer width="100%" height={210}>
          <BarChart
            data={chartData}
            margin={{
              top: 1,
              right: 5,
              left: 5,
              bottom: 10
            }}
          >
            <CartesianGrid stroke="var(--color-neutral-100)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={(props) => <CustomXAxisTick {...props} />}
              tickLine={{ stroke: 'var(--color-white)' }}
              axisLine={{ stroke: 'var(--color-neutral-100)' }}
              fontWeight={700}
              interval={0}
            />
            <YAxis hide allowDecimals={false} padding={{ top: 40 }} tickCount={4} />
            <Bar
              dataKey="value"
              fill="var(--color-secondary-navy-500)"
              radius={[10, 10, 0, 0]}
              maxBarSize={60}
            >
              <LabelList dataKey="value" position="top" content={CustomLabel} />
              {chartData.map((_entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    colorOverrides
                      ? colorOverrides[index % colorOverrides.length]
                      : COLORS[index % COLORS.length]
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <label className="flex items-center text-xs italic text-neutral-300">{subLabel}</label>
    </div>
  );
};

export default MetricsBarChartEntry;
